import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, ShieldCheck, SquarePen } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MasterTable, type MasterTableColumn } from '@/components/efs/MasterTable';
import { PageHeader } from '@/components/efs/PageHeader';
import { TableToolbar } from '@/components/efs/TableToolbar';

import { RoleCreateDialog } from './RoleCreateDialog';
import { useRolesList } from './useRoles';
import type { RoleDto } from './types';

const NS = 'settings';

export const RolesPage = () => {
  const { t } = useTranslation(NS);
  const navigate = useNavigate();
  const rolesQuery = useRolesList();
  const [createOpen, setCreateOpen] = useState(false);

  const roles = (rolesQuery.data ?? []).filter(
    (role) => role.active_flag && (role.assignable_flag || role.role_code === 'system_admin'),
  );

  const roleLabel = useCallback(
    (role: { role_code: string; display_name: string }) =>
      t(`efs.settings.users.roles.${role.role_code}`, {
        defaultValue: role.display_name || role.role_code,
      }),
    [t],
  );

  const columns = useMemo<MasterTableColumn<RoleDto>[]>(
    () => [
      {
        key: 'display_name',
        header: t('efs.settings.roles.fields.display_name'),
        cell: (role) => (
          <div>
            <div className="font-medium text-efs-text-primary">{roleLabel(role)}</div>
            <div className="mt-1 break-all text-xs text-efs-text-muted">{role.role_code}</div>
          </div>
        ),
      },
      {
        key: 'party_type',
        header: t('efs.settings.roles.fields.party_type'),
        cell: (role) =>
          role.party_type
            ? t(`efs.settings.companies.types.${role.party_type}`, {
                defaultValue: role.party_type,
              })
            : '-',
      },
      {
        key: 'permissions',
        header: t('efs.settings.roles.fields.permission_count'),
        align: 'right',
        cell: (role) =>
          t('efs.settings.roles.summary.permissions', { count: role.permissions.length }),
      },
      {
        key: 'status',
        header: t('efs.settings.users.fields.status'),
        cell: (role) => (
          <div className="flex flex-wrap gap-1">
            {role.system_role ? (
              <Badge variant="secondary">{t('efs.settings.roles.system_role')}</Badge>
            ) : null}
            {role.active_flag ? (
              <Badge variant="default">{t('efs.settings.users.fields.active')}</Badge>
            ) : (
              <Badge variant="outline">{t('efs.settings.roles.inactive')}</Badge>
            )}
          </div>
        ),
      },
      {
        key: 'actions',
        header: '',
        align: 'right',
        cell: (role) => (
          <Button asChild variant="outline" size="sm">
            <Link to={`/settings/roles/${encodeURIComponent(role.role_code)}/edit`}>
              <SquarePen className="h-4 w-4" />
              {t('efs.settings.roles.actions.edit_permissions')}
            </Link>
          </Button>
        ),
      },
    ],
    [roleLabel, t],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('efs.settings.roles.title')}
        subtitle={t('efs.settings.roles.subtitle')}
        count={roles.length}
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" />
            {t('efs.settings.roles.actions.create')}
          </Button>
        }
      />

      <section className="rounded-md border border-efs-border bg-efs-bg-panel">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-efs-text-primary">
              <ShieldCheck className="h-4 w-4" />
              {t('efs.settings.roles.role_list')}
            </div>
            <p className="mt-1 text-xs text-efs-text-muted">{t('efs.settings.roles.list_hint')}</p>
          </div>
        </div>
        <TableToolbar
          count={t('efs.common.messages.count', {
            ns: 'common',
            defaultValue: '{{count}} 件',
            count: roles.length,
          })}
        />
        <MasterTable
          columns={columns}
          rows={roles}
          rowKey={(role) => role.role_code}
          isLoading={rolesQuery.isLoading}
        />
      </section>

      <RoleCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(role) => navigate(`/settings/roles/${encodeURIComponent(role.role_code)}/edit`)}
      />
    </div>
  );
};

export default RolesPage;
